/**
 * LandingVariantsSection — the "Audience variants" block inside WebsiteTab's
 * Ad landing page card (docs/crm-landing-variants-plan.md).
 *
 * A variant is a named alternative landing experience for the SAME study:
 * /study/<slug>?v=<key> serves the variant's published form (+ optional copy
 * overrides) while every lead still lands in this study's pipeline. The
 * designated landing form above stays the default/control — an unknown or
 * paused variant falls back to it, never a 404.
 *
 * Unlike the card's batched marketing fields, variant CRUD saves IMMEDIATELY
 * (the FormsTab designate precedent) — each row is its own record, and the
 * copyable ?v= link must reflect what is really live. The A/B toggle PATCHes
 * the study immediately for the same reason.
 */
import { useCallback, useEffect, useState } from 'react';
import { Check, Copy, ExternalLink, Loader2, Pause, Pencil, Play, Plus, SplitSquareHorizontal, Trash2, X } from 'lucide-react';
import api from '../../../services/api';
import { CrmForm, LandingVariant } from '../../types';
import { useConfirm } from '../ui';

const inputCls =
  'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';
const labelCls = 'mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400';

const KEY_RE = /^[a-z0-9-]{1,40}$/;

const errMsg = (e: any): string =>
  e?.response?.data?.error || e?.message || 'Request failed';

interface EditorState {
  key: string;
  label: string;
  form_id: string;
  weight: string; // input-controlled; parsed on save
  condition: string;
  compensation_text: string;
  short_description: string;
}

const emptyEditor = (): EditorState => ({
  key: '', label: '', form_id: '', weight: '1',
  condition: '', compensation_text: '', short_description: '',
});

const editorFromVariant = (v: LandingVariant): EditorState => ({
  key: v.key,
  label: v.label,
  form_id: v.form_id,
  weight: String(v.weight ?? 1),
  condition: v.copy_overrides?.condition || '',
  compensation_text: v.copy_overrides?.compensation_text || '',
  short_description: v.copy_overrides?.short_description || '',
});

export default function LandingVariantsSection({
  studyId, studySlug, adUrlBase, publishableForms, experimentEnabled, onStudyChanged,
}: {
  studyId: string;
  studySlug: string;
  /** `${WCR_BASE}/study/${slug}` — the base the ?v= links append to. */
  adUrlBase: string;
  publishableForms: CrmForm[];
  experimentEnabled: boolean;
  /** Reload the study detail (the A/B toggle lives on the study row). */
  onStudyChanged: () => void;
}) {
  const confirm = useConfirm();
  const [variants, setVariants] = useState<LandingVariant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null); // variant id | 'create' | 'experiment'
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editor, setEditor] = useState<EditorState>(emptyEditor());
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/api/crm/studies/${studyId}/landing-variants`);
      setVariants(res.data?.variants || []);
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [studyId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/api/crm/studies/${studyId}/landing-variants`)
      .then((res) => { if (!cancelled) setVariants(res.data?.variants || []); })
      .catch((e) => { if (!cancelled) setError(errMsg(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [studyId]);

  const set = <K extends keyof EditorState>(key: K, value: string) =>
    setEditor((s) => ({ ...s, [key]: value }));

  const openCreate = () => {
    setEditor(emptyEditor());
    setEditingId(null);
    setAdding(true);
    setError(null);
  };
  const openEdit = (v: LandingVariant) => {
    setEditor(editorFromVariant(v));
    setEditingId(v.id);
    setAdding(false);
    setError(null);
  };
  const closeEditor = () => { setAdding(false); setEditingId(null); };

  const editorPayload = (forCreate: boolean) => {
    const weight = Number(editor.weight);
    return {
      ...(forCreate ? { key: editor.key.trim() } : {}),
      label: editor.label.trim(),
      form_id: editor.form_id,
      weight: Number.isInteger(weight) && weight >= 0 ? weight : 1,
      copy_overrides: {
        condition: editor.condition.trim(),
        compensation_text: editor.compensation_text.trim(),
        short_description: editor.short_description.trim(),
      },
    };
  };

  const saveEditor = async () => {
    const forCreate = !editingId;
    if (forCreate && !KEY_RE.test(editor.key.trim())) {
      setError('Key must be 1–40 lowercase letters, digits, and hyphens (it becomes the ?v= URL value).');
      return;
    }
    if (!editor.label.trim()) { setError('Give the variant a label.'); return; }
    if (!editor.form_id) { setError('Pick the form this variant serves.'); return; }
    setBusy(forCreate ? 'create' : editingId!);
    setError(null);
    try {
      if (forCreate) {
        await api.post(`/api/crm/studies/${studyId}/landing-variants`, editorPayload(true));
      } else {
        await api.patch(`/api/crm/studies/${studyId}/landing-variants/${editingId}`, editorPayload(false));
      }
      closeEditor();
      await load();
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setBusy(null);
    }
  };

  const toggleStatus = async (v: LandingVariant) => {
    setBusy(v.id);
    setError(null);
    try {
      await api.patch(`/api/crm/studies/${studyId}/landing-variants/${v.id}`, {
        status: v.status === 'active' ? 'paused' : 'active',
      });
      await load();
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setBusy(null);
    }
  };

  const remove = async (v: LandingVariant) => {
    if (!(await confirm({
      title: `Delete the "${v.label}" variant?`,
      message: `Ads still pointing at ?v=${v.key} will fall back to the default landing form. Past leads keep their recorded variant for reporting.`,
      confirmLabel: 'Delete variant',
      danger: true,
    }))) return;
    setBusy(v.id);
    setError(null);
    try {
      await api.delete(`/api/crm/studies/${studyId}/landing-variants/${v.id}`);
      await load();
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setBusy(null);
    }
  };

  const toggleExperiment = async () => {
    setBusy('experiment');
    setError(null);
    try {
      await api.patch(`/api/crm/studies/${studyId}`, { landing_experiment_enabled: !experimentEnabled });
      onStudyChanged();
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setBusy(null);
    }
  };

  const copyLink = async (v: LandingVariant) => {
    const url = `${adUrlBase}?v=${v.key}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedKey(v.key);
      setTimeout(() => setCopiedKey((k) => (k === v.key ? null : k)), 1500);
    } catch {
      // Clipboard blocked (permissions/HTTP): select-and-copy fallback is the
      // visible URL text next to the button.
    }
  };

  const formTitle = (formId: string) => {
    const f = publishableForms.find((x) => x.id === formId);
    return f ? `${f.title} (v${f.version})` : null;
  };

  const activeWeighted = variants.filter((v) => v.status === 'active' && v.weight > 0);
  const editorOpen = adding || editingId !== null;

  const editorPanel = (
    <div className="mt-2 space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/30">
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className={labelCls}>Key <span className="text-gray-400">(the ?v= URL value{editingId ? ' — fixed after create' : ''})</span></label>
          <input
            type="text" value={editor.key} placeholder="e.g. prediabetic" className={inputCls}
            disabled={!!editingId}
            onChange={(e) => set('key', e.target.value.toLowerCase())}
          />
        </div>
        <div>
          <label className={labelCls}>Label <span className="text-gray-400">(internal name)</span></label>
          <input type="text" value={editor.label} placeholder="e.g. Pre-diabetic audience" className={inputCls}
            onChange={(e) => set('label', e.target.value)} />
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div>
          <label className={labelCls}>Form this variant serves</label>
          <select value={editor.form_id} className={inputCls} onChange={(e) => set('form_id', e.target.value)}>
            <option value="">Choose a published form…</option>
            {publishableForms.map((f) => (
              <option key={f.id} value={f.id}>{f.title} (v{f.version})</option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>A/B weight <span className="text-gray-400">(0 = never auto-assigned)</span></label>
          <input type="number" min={0} max={1000} step={1} value={editor.weight} className={inputCls}
            onChange={(e) => set('weight', e.target.value)} />
        </div>
      </div>
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Landing copy overrides <span className="normal-case font-normal">(blank = use the study&apos;s copy above)</span>
        </p>
        <div className="space-y-2">
          <div>
            <label className={labelCls}>Condition (page headline reads “&lt;condition&gt; Study”)</label>
            <input type="text" value={editor.condition} placeholder="e.g. Pre-diabetes" className={inputCls}
              onChange={(e) => set('condition', e.target.value)} />
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Compensation</label>
              <input type="text" value={editor.compensation_text} placeholder="e.g. Up to $1,050" className={inputCls}
                onChange={(e) => set('compensation_text', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Short description</label>
              <input type="text" value={editor.short_description} className={inputCls}
                onChange={(e) => set('short_description', e.target.value)} />
            </div>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-end gap-2">
        <button
          type="button" onClick={closeEditor}
          className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
        >
          Cancel
        </button>
        <button
          type="button" onClick={saveEditor} disabled={busy !== null}
          className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:opacity-50"
        >
          {busy === 'create' || busy === editingId
            ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving…</>
            : editingId ? 'Save variant' : 'Add variant'}
        </button>
      </div>
    </div>
  );

  return (
    <div className="mt-4 border-t border-gray-100 pt-4 dark:border-gray-700">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-prose">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
            <SplitSquareHorizontal className="h-4 w-4 text-primary-600 dark:text-primary-400" /> Audience variants
          </h3>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Serve a different question set and landing copy per ad audience — leads still land in this study&apos;s
            pipeline. Point an ad set at <code className="text-[11px]">?v=&lt;key&gt;</code>; a paused or deleted
            variant falls back to the default form above.
          </p>
        </div>
        {!editorOpen && (
          <button
            type="button" onClick={openCreate} disabled={publishableForms.length === 0}
            title={publishableForms.length === 0 ? 'Publish a form on the Forms tab first' : undefined}
            className="inline-flex items-center gap-1.5 rounded-lg border border-primary-200 bg-primary-50 px-3 py-1.5 text-xs font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-primary-800 dark:bg-primary-900/30 dark:text-primary-300"
          >
            <Plus className="h-3.5 w-3.5" /> Add variant
          </button>
        )}
      </div>

      {loading ? (
        <div className="mt-3 flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading variants…
        </div>
      ) : variants.length === 0 && !adding ? (
        <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
          No variants yet — every visitor gets the default landing form.
        </p>
      ) : (
        <div className="mt-3 space-y-2">
          {variants.map((v) => (
            <div key={v.id} className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{v.label}</span>
                <code className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-600 dark:bg-gray-900/50 dark:text-gray-300">?v={v.key}</code>
                <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${
                  v.status === 'active'
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                }`}>
                  {v.status === 'active' ? 'Active' : 'Paused'}
                </span>
                {experimentEnabled && v.status === 'active' && v.weight > 0 && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">weight {v.weight}</span>
                )}
                <span className="ml-auto flex items-center gap-1">
                  <button
                    type="button" onClick={() => copyLink(v)} aria-label="Copy variant link"
                    title={`Copy ${adUrlBase}?v=${v.key}`}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-300 text-gray-600 transition-colors duration-150 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                  >
                    {copiedKey === v.key ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
                  </button>
                  <a
                    href={`${adUrlBase}?v=${v.key}`} target="_blank" rel="noopener noreferrer" aria-label="Open variant preview"
                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-300 text-gray-600 transition-colors duration-150 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                  <button
                    type="button" onClick={() => toggleStatus(v)} disabled={busy === v.id}
                    aria-label={v.status === 'active' ? 'Pause variant' : 'Activate variant'}
                    title={v.status === 'active' ? 'Pause — new visitors get the default form' : 'Activate'}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-300 text-gray-600 transition-colors duration-150 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                  >
                    {busy === v.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : v.status === 'active' ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                  </button>
                  <button
                    type="button" onClick={() => openEdit(v)} aria-label="Edit variant"
                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-300 text-gray-600 transition-colors duration-150 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button" onClick={() => remove(v)} disabled={busy === v.id} aria-label="Delete variant"
                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-300 text-gray-600 transition-colors duration-150 hover:bg-gray-50 hover:text-red-600 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </span>
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Serves {formTitle(v.form_id) || <span className="text-amber-700 dark:text-amber-400">a form that is not currently published — visitors get the default form until it is</span>}
                {v.copy_overrides?.condition ? <> · headline “{v.copy_overrides.condition} Study”</> : null}
              </p>
              {editingId === v.id && editorPanel}
            </div>
          ))}
        </div>
      )}

      {adding && editorPanel}

      {/* Same-URL A/B experiment */}
      <div className="mt-4 flex flex-wrap items-start justify-between gap-3 rounded-lg border border-gray-100 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/30">
        <div className="max-w-prose">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Same-URL A/B test</h4>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            When on, visitors arriving at <code className="text-[11px]">/study/{studySlug}</code> with no{' '}
            <code className="text-[11px]">?v=</code> are split (weighted, sticky per visitor) among the active
            variants above. Needs at least two active variants with weight &gt; 0
            {activeWeighted.length < 2 && (
              <span className="text-amber-700 dark:text-amber-400"> — only {activeWeighted.length} right now, so everyone gets the default form</span>
            )}.
            Explicit <code className="text-[11px]">?v=</code> links (your ad sets) always win.
          </p>
        </div>
        <button
          type="button" role="switch" aria-checked={experimentEnabled}
          onClick={toggleExperiment} disabled={busy === 'experiment'}
          className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 dark:focus:ring-offset-gray-800 ${
            experimentEnabled ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'
          }`}
        >
          <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-150 ${experimentEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
      </div>

      {error && (
        <p className="mt-2 flex items-start gap-1.5 text-xs text-red-600 dark:text-red-400">
          <X className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {error}
        </p>
      )}
    </div>
  );
}
